/**
 * Card - Componente de visualización de producto en mobile
 *
 * Este componente muestra la información de un producto en formato de tarjeta,
 * respetando las columnas seleccionadas por el usuario a través del ColumnSelector.
 * Las columnas 'name' y 'code' son fijas y siempre se muestran.
 */
import { Product } from '@shared/components/ui/Product';
import { DropdownActions } from '../interaction/DropdownActions';

interface CardProps {
  product: any; // Usa el tipo adecuado si lo tienes
  onReservedClick?: (product: any) => void;
  onStockClick?: (product: any) => void;
  selectedColumns?: string[];
}

export const Card = ({ product, onReservedClick, onStockClick, selectedColumns }: CardProps) => {
  const stock = product.stock || {};
  const totalStock = (stock.disponible ?? 0) + (stock.entrante ?? 0) + (stock.reservado ?? 0);

  // 🎯 Helper para verificar si una columna está visible
  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns) return true; // Si no hay selectedColumns, mostrar todo
    return selectedColumns.includes(columnId);
  };

  // 🎯 Verificar si hay alguna columna de stock visible
  const hasStockColumns =
    isColumnVisible('stock.disponible') || isColumnVisible('stock.reservado') || isColumnVisible('stock.total');

  return (
    <div className="p-4 pl-0 border-b border-card-border">
      {/* Header - Producto y acciones (siempre visible) */}
      <div className="mb-2 flex justify-content-between">
        <div className="flex-1">
          <Product
            id={product.id}
            name={product.name}
            src={product.image_hash}
            code={product.code}
            showImageModal={true}
          />
        </div>
        <div>
          <DropdownActions product={product} />
        </div>
      </div>

      {/* Costo y Fecha - Condicionales según columnas seleccionadas */}
      {(isColumnVisible('cost') || isColumnVisible('created_at')) && (
        <div className="flex items-center gap-3 mb-2">
          {isColumnVisible('cost') && (
            <span className="bg-card-border rounded-full px-4 py-1 text-sm font-medium">${product.cost ?? 0}</span>
          )}
          {isColumnVisible('created_at') && (
            <span className="text-xs ">{product.created_at ? new Date(product.created_at).toLocaleString() : '-'}</span>
          )}
        </div>
      )}

      {/* Cliente - Solo si está visible y tiene datos */}
      {isColumnVisible('client') && product.team && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-complementary-text">Cliente:</span>
          <span className="text-sm font-medium">{product.team.name}</span>
        </div>
      )}

      {/* Stock - Solo si al menos una columna de stock está visible */}
      {hasStockColumns && (
        <div className="flex gap-2 mt-2">
          {isColumnVisible('stock.disponible') && (
            <div className="flex-1 bg-card-bg rounded py-1 text-center border border-card-border">
              <div className="text-xs mb-1">Disponible</div>
              <div className="text-xs font-bold">{stock.disponible ?? 0}</div>
            </div>
          )}
          {isColumnVisible('stock.reservado') && (
            <div
              className="flex-1 bg-card-bg rounded py-1 text-center border border-card-border cursor-pointer hover:underline"
              onClick={() => onReservedClick && onReservedClick(product)}
            >
              <div className="text-xs mb-1">Reservado</div>
              <div className="text-xs font-bold">{stock.reservado ?? 0}</div>
            </div>
          )}
          {isColumnVisible('stock.total') && (
            <div
              className="flex-1 bg-card-bg rounded py-1 text-center border border-card-border cursor-pointer hover:underline"
              onClick={() => onStockClick && onStockClick(product)}
            >
              <div className="text-xs mb-1">Stock</div>
              <div className="text-xs font-bold">{totalStock}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
